// @ts-check
// Códigos de acesso ao cadastro no painel de suporte (contrato 08 de cobrança e operação, seção 7.1): gerar um código
// por instituição (mostrado uma vez, com link e mensagem pronta), acompanhar quem usou e revogar.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { createSignUpCode, listSignUpCodes, revokeSignUpCode } from '../../data/operations.js';
import { Button } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatDateTime } from '../../lib/format.js';
import { buildSignUpShareMessage, formatLocalDate } from '../../lib/sign-up-code-link.js';
import { FormError, SelectField, TextField } from '../settings/integration-ui.js';
import { copyToClipboard } from '../settings/settings-channels-page.js';
import { operationsErrorMessage, showOperationsError } from './operations-format.js';
import { maskSignUpAccessCode, SIGN_UP_CODE_LIMITS, SIGN_UP_CODE_STATUSES } from '../../../../shared/sign-up/sign-up-access-code.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/operations.js').SignUpCode} SignUpCode
 * @typedef {import('../../data/operations.js').SignUpCodeList} SignUpCodeList
 */

const STATUS_COLORS = /** @type {Record<string, string>} */ ({ ACTIVE: 'green', USED: 'blue', EXPIRED: 'gray', REVOKED: 'red' });
const RESTRICTIONS = /** @type {const} */ (['ANY', 'EMAIL', 'DOMAIN']);
// situação de criação que ainda não terminou: o código foi consumido, mas a escola não abriu
const PENDING_ACTIVATION_STATUSES = new Set(['PENDING_CREATION', 'ONGOING_CREATION']);


/** @param {string} text */
async function copyAndConfirm(text) {
  if (await copyToClipboard(text)) showSuccessSnackbar(t('operations.admin.signUpCodes.copied'));
}

/**
 * @param {{ list: SignUpCodeList, onClose: () => void, onCreated: (result: { signUpCode: SignUpCode, code: string, signUpUrl: string }) => void }} props
 */
function CreateSignUpCodeModal({ list, onClose, onCreated }) {
  const { defaults, plans } = list;
  const [form, setForm] = useState({
    label: '', note: '', restriction: /** @type {typeof RESTRICTIONS[number]} */ ('ANY'), allowedValue: '',
    expiresInDays: String(defaults.expiresInDays), maxUses: String(defaults.maxUses), subscriptionMode: /** @type {'TRIAL' | 'ACTIVE'} */ ('TRIAL'),
    planKey: '', trialDays: String(defaults.trialDays),
  });
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const update = (/** @type {keyof typeof form} */ key) => (/** @type {string} */ value) => setForm((current) => ({ ...current, [key]: value }));
  const defaultPlanName = plans.find((plan) => plan.key === defaults.planKey)?.name ?? defaults.planKey;
  const isValid = form.label.trim().length >= SIGN_UP_CODE_LIMITS.minimumLabelLength && (form.restriction === 'ANY' || form.allowedValue.trim() !== '');

  const submit = async () => {
    setIsSaving(true);
    setError(null);
    try {
      onCreated(await createSignUpCode({
        label: form.label.trim(),
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
        ...(form.restriction === 'EMAIL' ? { allowedEmail: form.allowedValue.trim() } : {}),
        ...(form.restriction === 'DOMAIN' ? { allowedEmailDomain: form.allowedValue.trim() } : {}),
        expiresInDays: Number(form.expiresInDays),
        maxUses: Number(form.maxUses),
        subscriptionMode: form.subscriptionMode,
        ...(form.planKey ? { planKey: form.planKey } : {}),
        ...(form.subscriptionMode === 'TRIAL' ? { trialDays: Number(form.trialDays) } : {}),
      }));
    } catch (requestError) {
      setError(operationsErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  return html`<${Modal} title=${t('operations.admin.signUpCodes.createTitle')} subtitle=${t('operations.admin.signUpCodes.createSubtitle')}
    confirmLabel=${t('operations.admin.signUpCodes.create')} isConfirmDisabled=${!isValid} isLoading=${isSaving} onClose=${onClose} onConfirm=${submit}>
    <div class="integration-modal-body admin-code-form">
      <${TextField} label=${t('operations.admin.signUpCodes.fields.label')} value=${form.label} onInput=${update('label')} isRequired=${true}
        placeholder=${t('operations.admin.signUpCodes.fields.labelPlaceholder')} maxLength=${SIGN_UP_CODE_LIMITS.maximumLabelLength} autoFocus=${true} />
      <${TextField} label=${t('operations.admin.signUpCodes.fields.note')} value=${form.note} onInput=${update('note')} multiline=${true}
        placeholder=${t('operations.admin.signUpCodes.fields.notePlaceholder')} maxLength=${SIGN_UP_CODE_LIMITS.maximumNoteLength} />
      <div class="admin-code-form-row">
        <${SelectField} label=${t('operations.admin.signUpCodes.fields.restriction')} value=${form.restriction} onChange=${update('restriction')}
          options=${RESTRICTIONS.map((restriction) => ({ value: restriction, label: t(`operations.admin.signUpCodes.restrictions.${restriction}`) }))} />
        ${form.restriction === 'EMAIL'
          ? html`<${TextField} label=${t('operations.admin.signUpCodes.fields.allowedEmail')} type="email" value=${form.allowedValue} onInput=${update('allowedValue')}
              placeholder=${t('operations.admin.signUpCodes.fields.allowedEmailPlaceholder')} isRequired=${true} />`
          : form.restriction === 'DOMAIN'
            ? html`<${TextField} label=${t('operations.admin.signUpCodes.fields.allowedEmailDomain')} value=${form.allowedValue} onInput=${update('allowedValue')}
                placeholder=${t('operations.admin.signUpCodes.fields.allowedEmailDomainPlaceholder')} isRequired=${true} />`
            : null}
      </div>
      <div class="admin-code-form-row">
        <${TextField} label=${t('operations.admin.signUpCodes.fields.expiresInDays')} type="number" value=${form.expiresInDays} onInput=${update('expiresInDays')}
          hint=${t('operations.admin.signUpCodes.fields.expiresInDaysHint')} />
        <${TextField} label=${t('operations.admin.signUpCodes.fields.maxUses')} type="number" value=${form.maxUses} onInput=${update('maxUses')}
          hint=${t('operations.admin.signUpCodes.fields.maxUsesHint')} />
      </div>
      <div class="admin-code-form-row">
        <${SelectField} label=${t('operations.admin.signUpCodes.fields.subscriptionMode')} value=${form.subscriptionMode} onChange=${update('subscriptionMode')}
          options=${['TRIAL', 'ACTIVE'].map((mode) => ({ value: mode, label: t(`operations.admin.signUpCodes.subscriptionModes.${mode}`) }))} />
        <${SelectField} label=${t('operations.admin.signUpCodes.fields.planKey')} value=${form.planKey} onChange=${update('planKey')}
          options=${[{ value: '', label: t('operations.admin.signUpCodes.fields.planDefault', { name: defaultPlanName }) }, ...plans.map((plan) => ({ value: plan.key, label: plan.name }))]} />
      </div>
      ${form.subscriptionMode === 'TRIAL'
        ? html`<${TextField} label=${t('operations.admin.signUpCodes.fields.trialDays')} type="number" value=${form.trialDays} onInput=${update('trialDays')}
            hint=${t('operations.admin.signUpCodes.fields.trialDaysHint', { days: defaults.trialDays })} />`
        : null}
      <${FormError} message=${error} />
    </div>
  <//>`;
}

/**
 * O código só existe aqui, no estado do diálogo: fechar o diálogo o descarta.
 * @param {{ created: { signUpCode: SignUpCode, code: string, signUpUrl: string }, onClose: () => void }} props
 */
function CreatedSignUpCodeModal({ created, onClose }) {
  const { signUpCode, code, signUpUrl } = created;
  const message = buildSignUpShareMessage({ signUpUrl, code, expiresAt: signUpCode.expiresAt, allowedEmail: signUpCode.allowedEmail });
  return html`<${Modal} title=${t('operations.admin.signUpCodes.createdTitle')} subtitle=${signUpCode.label}
    confirmLabel=${t('operations.admin.signUpCodes.done')} onClose=${onClose} onConfirm=${onClose} hideCancel=${true}>
    <div class="integration-modal-body admin-code-created">
      <code class="admin-code-value" aria-label=${t('operations.admin.signUpCodes.columns.code')}>${code}</code>
      <p class="integration-warning"><${Icon} name="IconAlertTriangle" size=${14} />${t('operations.admin.signUpCodes.createdWarning')}</p>
      <div class="admin-code-copy-actions">
        <${Button} title=${t('operations.admin.signUpCodes.copyCode')} icon="IconCopy" variant="secondary" size="small" onClick=${() => copyAndConfirm(code)} />
        <${Button} title=${t('operations.admin.signUpCodes.copyLink')} icon="IconLink" variant="secondary" size="small" onClick=${() => copyAndConfirm(signUpUrl)} />
        <${Button} title=${t('operations.admin.signUpCodes.copyMessage')} icon="IconMessage" accent="blue" size="small" onClick=${() => copyAndConfirm(message)} />
      </div>
      <div class="admin-code-message">
        <span class="form-field-label">${t('operations.admin.signUpCodes.messagePreview')}</span>
        <p>${message}</p>
      </div>
    </div>
  <//>`;
}

/** @param {{ signUpCode: SignUpCode }} props */
function RedemptionList({ signUpCode }) {
  if (signUpCode.redemptions.length === 0) return html`<span class="settings-table-muted">${t('operations.admin.signUpCodes.notUsed')}</span>`;
  return html`<span class="admin-code-redemptions">${signUpCode.redemptions.map((redemption) => html`<span key=${redemption.tenantId}>
    <span>${redemption.tenantSlug ?? t('operations.admin.signUpCodes.removedInstitution')}</span>
    <span class="settings-table-muted">
      ${formatLocalDate(redemption.redeemedAt)}
      ${redemption.tenantActivationStatus && PENDING_ACTIVATION_STATUSES.has(redemption.tenantActivationStatus) ? ` · ${t('operations.admin.signUpCodes.pendingCreation')}` : ''}
    </span>
  </span>`)}</span>`;
}

export function SignUpCodesSection() {
  const [list, setList] = useState(/** @type {SignUpCodeList | null} */ (null));
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isCreating, setIsCreating] = useState(false);
  const [created, setCreated] = useState(/** @type {{ signUpCode: SignUpCode, code: string, signUpUrl: string } | null} */ (null));
  const [revoking, setRevoking] = useState(/** @type {SignUpCode | null} */ (null));
  const [isRevoking, setIsRevoking] = useState(false);

  const load = () => listSignUpCodes({ status: statusFilter }).then(setList).catch(showOperationsError);
  useEffect(() => {
    load();
  }, [statusFilter]);

  const revoke = async () => {
    if (!revoking) return;
    setIsRevoking(true);
    try {
      await revokeSignUpCode(revoking.id);
      showSuccessSnackbar(t('operations.admin.signUpCodes.revoked'));
      setRevoking(null);
      load();
    } catch (error) {
      showOperationsError(error);
    } finally {
      setIsRevoking(false);
    }
  };

  return html`<section class="admin-sign-up-codes" aria-labelledby="admin-sign-up-codes-title">
    <div class="admin-section-header">
      <h2 class="settings-section-title" id="admin-sign-up-codes-title">${t('operations.admin.signUpCodes.title')}</h2>
      <div class="admin-section-actions">
        <select class="text-input admin-code-filter" value=${statusFilter} aria-label=${t('operations.admin.signUpCodes.filter')}
          onChange=${(/** @type {Event} */ event) => setStatusFilter(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
          <option value="ALL" selected=${statusFilter === 'ALL'}>${t('operations.admin.signUpCodes.allStatuses')}</option>
          ${SIGN_UP_CODE_STATUSES.map((status) => html`<option key=${status} value=${status} selected=${statusFilter === status}>${t(`operations.admin.signUpCodes.statuses.${status}`)}</option>`)}
        </select>
        <${Button} title=${t('operations.admin.signUpCodes.create')} icon="IconPlus" accent="blue" size="small" disabled=${!list} onClick=${() => setIsCreating(true)} />
      </div>
    </div>
    <p class="channel-card-description">${t('operations.admin.signUpCodes.description')}</p>
    ${list === null
      ? html`<${Skeleton} height=${120} radius=${8} />`
      : list.codes.length === 0
        ? html`<p class="channel-card-description">${t('operations.admin.signUpCodes.empty')}</p>`
        : html`<div class="settings-table admin-code-table">
            <div class="settings-table-row settings-table-header admin-code-row">
              <span>${t('operations.admin.signUpCodes.columns.code')}</span>
              <span>${t('operations.admin.signUpCodes.columns.label')}</span>
              <span>${t('operations.admin.signUpCodes.columns.status')}</span>
              <span class="settings-table-number">${t('operations.admin.signUpCodes.columns.uses')}</span>
              <span>${t('operations.admin.signUpCodes.columns.expiresAt')}</span>
              <span>${t('operations.admin.signUpCodes.columns.usedBy')}</span>
              <span></span>
            </div>
            ${list.codes.map((signUpCode) => html`<div key=${signUpCode.id} class="settings-table-row admin-code-row" title=${signUpCode.note ?? ''}>
              <span class="settings-table-mono admin-code-mask">${maskSignUpAccessCode(signUpCode.codeHint)}</span>
              <span class="admin-code-label">
                <span>${signUpCode.label}</span>
                <span class="settings-table-muted">
                  ${[
                    t(`operations.admin.signUpCodes.subscriptionModes.${signUpCode.subscriptionMode}`),
                    signUpCode.allowedEmail || signUpCode.allowedEmailDomain
                      ? t('operations.admin.signUpCodes.restrictedTo', { value: signUpCode.allowedEmail ?? `@${signUpCode.allowedEmailDomain}` })
                      : null,
                    t('operations.admin.signUpCodes.createdBy', { email: signUpCode.createdByEmail }),
                  ].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span><${Tag} color=${STATUS_COLORS[signUpCode.status] ?? 'gray'} text=${t(`operations.admin.signUpCodes.statuses.${signUpCode.status}`)} /></span>
              <span class="settings-table-number">${t('operations.admin.signUpCodes.uses', { used: signUpCode.useCount, max: signUpCode.maxUses })}</span>
              <span class="settings-table-muted" title=${formatDateTime(signUpCode.expiresAt)}>${formatLocalDate(signUpCode.expiresAt)}</span>
              <span><${RedemptionList} signUpCode=${signUpCode} /></span>
              <span class="admin-code-actions">${signUpCode.status === 'ACTIVE'
                ? html`<${Button} title=${t('operations.admin.signUpCodes.revoke')} variant="secondary" accent="danger" size="small" onClick=${() => setRevoking(signUpCode)} />`
                : null}</span>
            </div>`)}
          </div>`}
    ${isCreating && list
      ? html`<${CreateSignUpCodeModal} list=${list} onClose=${() => setIsCreating(false)} onCreated=${(/** @type {any} */ result) => {
          setIsCreating(false);
          setCreated(result);
          load();
        }} />`
      : null}
    ${created ? html`<${CreatedSignUpCodeModal} created=${created} onClose=${() => setCreated(null)} />` : null}
    ${revoking
      ? html`<${Modal} title=${t('operations.admin.signUpCodes.revokeTitle', { hint: revoking.codeHint })} subtitle=${t('operations.admin.signUpCodes.revokeConfirm')}
          confirmLabel=${t('operations.admin.signUpCodes.revoke')} confirmAccent="danger" isLoading=${isRevoking} onClose=${() => setRevoking(null)} onConfirm=${revoke} />`
      : null}
  </section>`;
}
